import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import http from "../../services/http";

/* ─── CSV helper ─── */
const downloadCSV = (filename, rows, headers) => {
  const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [
    headers.map(escape).join(","),
    ...rows.map((r) => headers.map((h) => escape(r[h])).join(",")),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const fmt     = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const fmtSign = (n) => (n >= 0 ? "+" : "") + fmt(n);

const betTypeMeta = {
  match_bet:     { label: "Match Bet",     bg: "#eff6ff", color: "#2563eb" },
  question_bet:  { label: "Question Bet",  bg: "#f0fdf4", color: "#16a34a" },
  selection_bet: { label: "Selection Bet", bg: "#fdf4ff", color: "#9333ea" },
};

const statusMeta = {
  won:     { bg: "#dcfce7", color: "#16a34a" },
  winner:  { bg: "#dcfce7", color: "#16a34a" },
  lost:    { bg: "#fee2e2", color: "#dc2626" },
  loser:   { bg: "#fee2e2", color: "#dc2626" },
  refunded:{ bg: "#fef9c3", color: "#92400e" },
  refund:  { bg: "#fef9c3", color: "#92400e" },
};
const statusBadge = (s = "") => {
  const m = statusMeta[s.toLowerCase()] || { bg: "#f1f5f9", color: "#64748b" };
  return <span style={{ ...ts.badge, ...m }}>{s || "pending"}</span>;
};
const betTypeBadge = (t = "") => {
  const m = betTypeMeta[t] || { label: t, bg: "#f1f5f9", color: "#64748b" };
  return <span style={{ ...ts.badge, background: m.bg, color: m.color }}>{m.label}</span>;
};

export const UserBetsReportLayer = () => {
  const [users,      setUsers]      = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userQuery,  setUserQuery]  = useState("");
  const [dropOpen,   setDropOpen]   = useState(false);
  const [searching,  setSearching]  = useState(false);
  const debounceRef = useRef(null);

  const [reportData, setReportData] = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [tab,        setTab]        = useState("section_a");

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo,   setDateTo]   = useState("");
  const [search,   setSearch]   = useState("");

  /* ── Load initial user list ── */
  useEffect(() => {
    http.get("reports_user_bets").then((res) => {
      if (res.data?.status) setUsers(res.data.users || []);
    }).catch(() => {});
  }, []);

  /* ── Debounced user search ── */
  const handleUserQuery = (e) => {
    const val = e.target.value;
    setUserQuery(val);
    setDropOpen(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await http.get("add_user", { params: { search: val } });
        if (res.data?.status) setUsers(res.data.data || []);
      } finally { setSearching(false); }
    }, 350);
  };

  const selectUser = (u) => {
    setSelectedUser(u);
    setUserQuery(u.name + (u.mobile ? `  •  ${u.mobile}` : ""));
    setDropOpen(false);
    setReportData(null);
  };

  /* ── Fetch report for selected user ── */
  const fetchReport = useCallback(async () => {
    if (!selectedUser) return;
    setLoading(true);
    try {
      const params = { user_id: selectedUser.id };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo)   params.date_to   = dateTo;
      const res = await http.get("reports_user_bets", { params });
      if (res.data?.status) {
        setReportData(res.data);
      } else {
        toast.error(res.data?.msg || "Failed to load report");
      }
    } catch {
      toast.error("Server error");
    } finally {
      setLoading(false);
    }
  }, [selectedUser, dateFrom, dateTo]);

  useEffect(() => { if (selectedUser) fetchReport(); }, [fetchReport]);

  /* ── Filtered rows ── */
  const rows = useMemo(() => {
    if (!reportData) return [];
    const source = tab === "section_a" ? (reportData.section_a || []) : (reportData.section_c || []);
    if (!search.trim()) return source;
    const v = search.toLowerCase();
    return source.filter((r) =>
      (r.match_name || "").toLowerCase().includes(v) ||
      (r.question_title || "").toLowerCase().includes(v) ||
      (r.status || "").toLowerCase().includes(v)
    );
  }, [reportData, tab, search]);

  /* ── Export ── */
  const handleExport = () => {
    if (!rows.length) { toast.warn("Nothing to export"); return; }
    const all = [...(reportData?.section_a || []), ...(reportData?.section_c || [])];
    const headers = ["bet_id", "bet_type", "match_name", "question_title",
      "side", "amount", "win_amount", "status", "created_at"];
    downloadCSV(
      `UserBets_${selectedUser?.name || "user"}_${new Date().toISOString().slice(0,10)}.csv`,
      all, headers
    );
    toast.success("CSV exported");
  };

  const summary = reportData?.summary || {};

  return (
    <div style={s.root}>
      {/* ── Header ── */}
      <div style={s.headerCard}>
        <div style={s.headerLeft}>
          <div style={s.iconWrap}>
            <Icon icon="solar:user-speak-bold-duotone" style={{ fontSize: 24 }} />
          </div>
          <div>
            <h4 style={s.pageTitle}>User Bets Report</h4>
            <p style={s.pageSub}>View section-wise bets for any user — Section A &amp; C</p>
          </div>
        </div>
        {reportData && (
          <button style={s.exportBtn} onClick={handleExport}>
            <Icon icon="solar:file-download-bold" style={{ fontSize: 16 }} />
            Export CSV
          </button>
        )}
      </div>

      {/* ── User Selector + Filters ── */}
      <div style={s.filterCard}>
        <div style={{ flex: "1 1 260px", position: "relative" }}>
          <label style={s.label}>Select User</label>
          {!selectedUser ? (
            <>
              <input
                style={s.input} placeholder="Search by name or mobile…"
                value={userQuery} onChange={handleUserQuery}
                onFocus={() => setDropOpen(true)}
                onBlur={() => setTimeout(() => setDropOpen(false), 150)}
                autoComplete="off"
              />
              {dropOpen && (searching || users.length > 0) && (
                <div style={s.dropdown}>
                  {searching && (
                    <div style={{ padding: "9px 14px", color: "#94a3b8", fontSize: "0.8rem", display: "flex", gap: 6 }}>
                      <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} />
                      Searching…
                    </div>
                  )}
                  {!searching && users.slice(0, 20).map((u) => (
                    <div key={u.id} style={s.dropItem}
                      onMouseDown={() => selectUser(u)}
                      onMouseEnter={(e) => e.currentTarget.style.background = "#f0fdf4"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
                    >
                      <span style={{ fontWeight: 600, color: "#1e293b" }}>{u.name}</span>
                      <span style={{ fontSize: "0.75rem", color: "#94a3b8", marginLeft: 8 }}>
                        {u.mobile}  <span style={{ color: "#cbd5e1" }}>#{u.id}</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div style={s.userCard}>
              <div style={s.userAvatar}>
                <Icon icon="solar:user-bold-duotone" style={{ fontSize: 20, color: "#16a34a" }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: "#14532d", fontSize: "0.88rem" }}>{selectedUser.name}</div>
                <div style={{ fontSize: "0.75rem", color: "#64748b" }}>{selectedUser.mobile} · {selectedUser.uid}</div>
              </div>
              <button style={s.clearBtn} onClick={() => { setSelectedUser(null); setUserQuery(""); setReportData(null); }}>
                <Icon icon="radix-icons:cross-2" style={{ fontSize: 13 }} />
              </button>
            </div>
          )}
        </div>
        <div style={{ flex: "0 0 150px" }}>
          <label style={s.label}>From Date</label>
          <input type="date" style={s.input} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div style={{ flex: "0 0 150px" }}>
          <label style={s.label}>To Date</label>
          <input type="date" style={s.input} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
        {selectedUser && (
          <div style={{ alignSelf: "flex-end" }}>
            <button style={s.loadBtn} onClick={fetchReport} disabled={loading}>
              {loading ? <span className="spinner-border spinner-border-sm" style={{ width: 14, height: 14 }} /> : <Icon icon="solar:refresh-bold" style={{ fontSize: 15 }} />}
              Load Report
            </button>
          </div>
        )}
      </div>

      {/* ── Not selected ── */}
      {!selectedUser && (
        <div style={s.emptyState}>
          <Icon icon="solar:user-speak-bold-duotone" style={{ fontSize: 52, color: "#cbd5e1", display: "block", margin: "0 auto 12px" }} />
          <div style={{ fontWeight: 700, color: "#94a3b8", fontSize: "0.95rem" }}>Select a user above to view their bet history</div>
        </div>
      )}

      {/* ── Summary cards ── */}
      {reportData && (
        <>
          <div style={s.summaryGrid}>
            {[
              { label: "Total Bets",     value: (summary.total_bets || 0).toLocaleString(),    icon: "solar:gamepad-bold-duotone",      color: "#3b82f6" },
              { label: "Total Invested", value: fmt(summary.total_invested),                    icon: "solar:wallet-bold-duotone",      color: "#8b5cf6" },
              { label: "Total Won",      value: fmt(summary.total_won),                         icon: "solar:cup-bold-duotone",         color: "#16a34a" },
              { label: "Total Refund",   value: fmt(summary.total_refund),                      icon: "solar:refresh-bold-duotone",     color: "#f59e0b" },
              {
                label: "Net P&L",
                value: fmtSign(summary.net_pnl || 0),
                icon: "solar:chart-2-bold-duotone",
                color: (summary.net_pnl || 0) >= 0 ? "#16a34a" : "#dc2626",
              },
            ].map((c) => (
              <div key={c.label} style={s.sumCard}>
                <div style={{ ...s.sumIcon, background: `${c.color}18`, color: c.color }}>
                  <Icon icon={c.icon} style={{ fontSize: 20 }} />
                </div>
                <div>
                  <div style={{ ...s.sumValue, color: c.color }}>{c.value}</div>
                  <div style={s.sumLabel}>{c.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Tabs ── */}
          <div style={s.tabRow}>
            {[
              { key: "section_a", label: "Section A Bets", count: reportData.section_a?.length || 0 },
              { key: "section_c", label: "Section C Bets", count: reportData.section_c?.length || 0 },
            ].map((t) => (
              <button key={t.key} style={{ ...s.tabBtn, ...(tab === t.key ? s.tabActive : {}) }} onClick={() => setTab(t.key)}>
                {t.label}
                <span style={{ ...s.tabCount, background: tab === t.key ? "rgba(255,255,255,0.25)" : "#f1f5f9" }}>
                  {t.key === tab ? rows.length : t.count}
                </span>
              </button>
            ))}

            <div style={{ marginLeft: "auto" }}>
              <div style={{ position: "relative" }}>
                <Icon icon="ion:search-outline" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: "#94a3b8", pointerEvents: "none" }} />
                <input style={{ ...s.input, paddingLeft: 32, width: 200 }} placeholder="Filter table…" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
            </div>
          </div>

          {/* ── Table ── */}
          <div style={s.card}>
            {loading ? (
              <div style={s.emptyCell}>
                <span className="spinner-border spinner-border-sm" style={{ marginRight: 8 }} />
                Loading bets…
              </div>
            ) : rows.length === 0 ? (
              <div style={s.emptyCell}>
                <Icon icon="solar:gamepad-bold-duotone" style={{ fontSize: 40, color: "#cbd5e1", display: "block", margin: "0 auto 10px" }} />
                No bets found
              </div>
            ) : (
              <div style={s.tableWrap}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>#</th>
                      <th style={s.th}>Bet Type</th>
                      <th style={s.th}>Match</th>
                      <th style={s.th}>Question</th>
                      <th style={s.th}>Side / Option</th>
                      <th style={{ ...s.th, textAlign: "right" }}>Invested</th>
                      <th style={{ ...s.th, textAlign: "right" }}>Won</th>
                      <th style={s.th}>Status</th>
                      <th style={s.th}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, idx) => (
                      <tr key={row.bet_id + "-" + idx}
                        style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "#f0fdf4"}
                        onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafbfc"}
                      >
                        <td style={s.td}><span style={{ color: "#94a3b8", fontSize: "0.75rem" }}>{idx + 1}</span></td>
                        <td style={s.td}>{betTypeBadge(row.bet_type)}</td>
                        <td style={s.td}>
                          <span style={{ fontWeight: 600, color: "#0f172a", fontSize: "0.82rem" }}>{row.match_name || "—"}</span>
                        </td>
                        <td style={s.td}>
                          <span style={{ fontSize: "0.8rem", color: "#475569" }}>{row.question_title || "—"}</span>
                        </td>
                        <td style={s.td}>
                          {row.side || row.selected_option
                            ? <span style={{ fontFamily: "monospace", fontWeight: 600, color: "#475569" }}>{row.side || row.selected_option}</span>
                            : <span style={{ color: "#cbd5e1" }}>—</span>
                          }
                        </td>
                        <td style={{ ...s.td, textAlign: "right" }}>
                          <span style={{ fontWeight: 700, color: "#8b5cf6" }}>{fmt(row.amount)}</span>
                        </td>
                        <td style={{ ...s.td, textAlign: "right" }}>
                          <span style={{ fontWeight: 700, color: row.win_amount > 0 ? "#16a34a" : "#94a3b8" }}>
                            {row.win_amount > 0 ? fmt(row.win_amount) : "—"}
                          </span>
                        </td>
                        <td style={s.td}>{statusBadge(row.status)}</td>
                        <td style={s.td}>
                          <span style={{ fontSize: "0.77rem", color: "#64748b" }}>
                            {row.created_at ? new Date(row.created_at).toLocaleString("en-IN") : "—"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/* ─── Styles ─── */
const s = {
  root: { padding: "20px 24px 40px", background: "#f1f5f9", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
  headerCard: { background: "#fff", borderRadius: 16, padding: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", border: "1px solid #e8edf2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", marginBottom: 20, flexWrap: "wrap", gap: 12 },
  headerLeft: { display: "flex", alignItems: "center", gap: 14 },
  iconWrap: { width: 48, height: 48, borderRadius: 12, background: "linear-gradient(135deg,#16632b,#1da144)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  pageTitle: { margin: 0, fontWeight: 800, fontSize: "1.35rem", color: "#0f172a" },
  pageSub: { margin: "3px 0 0", fontSize: "0.82rem", color: "#64748b" },
  exportBtn: { display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", background: "linear-gradient(135deg,#16632b,#1da144)", border: "none", borderRadius: 9, color: "#fff", fontSize: "0.82rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },

  filterCard: { background: "#fff", borderRadius: 14, padding: "16px 20px", display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap", border: "1px solid #e8edf2", marginBottom: 20 },
  label: { display: "block", fontSize: "0.72rem", fontWeight: 700, color: "#475569", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.04em" },
  input: { width: "100%", padding: "8px 12px", border: "1.5px solid #e2e8f0", borderRadius: 9, fontSize: "0.84rem", color: "#1e293b", background: "#f8fafc", outline: "none", fontFamily: "inherit", boxSizing: "border-box" },
  loadBtn: { display: "flex", alignItems: "center", gap: 6, padding: "9px 18px", background: "linear-gradient(135deg,#16632b,#1da144)", border: "none", borderRadius: 9, color: "#fff", fontSize: "0.84rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" },

  dropdown: { position: "absolute", top: "100%", left: 0, right: 0, zIndex: 1050, background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 9, boxShadow: "0 4px 16px rgba(0,0,0,0.10)", maxHeight: 220, overflowY: "auto", marginTop: 2 },
  dropItem: { padding: "9px 14px", cursor: "pointer", background: "#fff", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 4 },
  userCard: { display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", background: "#f0fdf4", border: "1.5px solid #bbf7d0", borderRadius: 10 },
  userAvatar: { width: 34, height: 34, borderRadius: "50%", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  clearBtn: { background: "#fee2e2", border: "none", borderRadius: "50%", width: 24, height: 24, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#dc2626", flexShrink: 0 },

  emptyState: { background: "#fff", borderRadius: 16, padding: "60px 20px", textAlign: "center", border: "1px solid #e8edf2" },
  summaryGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 16 },
  sumCard: { background: "#fff", borderRadius: 14, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, border: "1px solid #e8edf2" },
  sumIcon: { width: 40, height: 40, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  sumValue: { fontSize: "1.2rem", fontWeight: 800, lineHeight: 1 },
  sumLabel: { fontSize: "0.67rem", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginTop: 2 },

  tabRow: { display: "flex", gap: 8, marginBottom: 14, alignItems: "center", flexWrap: "wrap" },
  tabBtn: { display: "flex", alignItems: "center", padding: "8px 14px", border: "1.5px solid #e2e8f0", background: "#fff", borderRadius: 10, cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, color: "#475569", fontFamily: "inherit" },
  tabActive: { background: "linear-gradient(135deg,#0f4c25,#16632b)", color: "#fff", border: "none" },
  tabCount: { marginLeft: 8, padding: "2px 7px", borderRadius: 100, fontSize: "0.68rem", fontWeight: 700 },

  card: { background: "#fff", borderRadius: 16, border: "1px solid #e8edf2", overflow: "hidden" },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "0.81rem" },
  th: { padding: "10px 14px", background: "#f8fafc", color: "#64748b", fontWeight: 700, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1.5px solid #f1f5f9", whiteSpace: "nowrap" },
  td: { padding: "10px 14px", borderBottom: "1px solid #f8fafc", color: "#334155", verticalAlign: "middle" },
  emptyCell: { padding: "60px 20px", textAlign: "center", color: "#94a3b8", fontSize: "0.85rem" },
};

const ts = {
  badge: { display: "inline-flex", alignItems: "center", padding: "3px 9px", borderRadius: 100, fontSize: "0.7rem", fontWeight: 700, textTransform: "capitalize" },
};
